import { SetupInstance } from '../../app/msal-account-helpers';
import { protectedResources } from '../../authConfig';
import { useLocal } from './../environment';

import { InteractionRequiredAuthError, InteractionStatus, BrowserAuthError } from '@azure/msal-browser';
import { hydrateObjects, cleanUpJsonForTransport } from '../json-helper';
import polly from 'polly-js';
import { AccessTokenHelper } from '../access-token-helper';
import { msalConfig } from '../../app';

export class NotificationsAPI {
    constructor(instance, inProgress, accounts, forceRefresh) {

        this.instance = SetupInstance(instance);
        this.inProgress = inProgress;
        this.account = accounts[0];

        this.accessTokenRequest = {
            scopes: [
                ...protectedResources.apiProperty.scopes.read
            ],
            authority: `https://login.microsoftonline.com/${msalConfig.auth.tenantId}/`,
            forceRefresh: forceRefresh,
            account: this.account,
        };

        this.accessTokenService = new AccessTokenHelper();
    }

    baseUrl = useLocal ?
        "http://localhost:8083/api/notifications" :
        "https://apim.ianrobertson.co.uk/api/notifications/v1";

    getMyNotifications = includeDismissed => {

        // Create a variable to store auth errors that shouldn't be retried
        let authError = null;

        return polly()
            .logger(error => {
                console.error("An error occurred in getMyNotifications", error); // will be hit 5 times if issues
            })
            .handle(err => {
                // If we've already detected an auth error, don't retry
                if (authError) return false;

                // Don't retry for 401/403 errors
                if (err.status === 401 || err.status === 403) {
                    authError = err; // Store the auth error
                    console.log(`Authorization error (${err.status}), not retrying`);
                    return false; // Don't retry
                }

                return true; // Retry for other errors
            })
            .waitAndRetry(5)
            .executeForPromise(() =>  {
                return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars

                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(async accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "GET",
                                headers: headers
                            };

                            try {
                                const response = await fetch(`${this.baseUrl}/GetMyNotifications${includeDismissed ? '/?includeDismissed=true' : ''}`, options);
                                
                                if (!response.ok) {
                                    return reject(new Error(`HTTP error! Status: ${response.status}`));
                                }
                                
                                const data = await response.json();
                                const result = cleanUpJsonForTransport(Object.assign([], hydrateObjects(data)));
                                return resolve(result);
                            } catch (error) {
                                console.log(error);
                                return reject(error);
                            }
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });
                });
            });
    };

    dismissNotification = notification => {

        return polly()
            .logger(error => {
                console.error("An error occurred in dismissNotification", error); // will be hit 5 times if issues
            })
            .waitAndRetry(5)
            .executeForPromise(() => {
                return new Promise((resolve, reject) => { // eslint-disable-line no-unused-vars

                    if (this.inProgress !== InteractionStatus.None) {
                        return this.instance.acquireTokenSilent(this.accessTokenRequest);
                    }

                    this.accessTokenService.getAccessToken(this.instance, this.inProgress, this.accessTokenRequest)
                        .then(accessTokenResponse => {

                            if (!!!accessTokenResponse) {
                                return reject("No access token");
                            }

                            const bearer = `Bearer ${accessTokenResponse.accessToken}`;

                            const headers = new Headers();
                            headers.append("Authorization", bearer);
                            headers.append("Content-Type", "application/json");

                            const options = {
                                method: "PATCH",
                                headers: headers,
                                body: JSON.stringify(cleanUpJsonForTransport(notification))
                            };

                            fetch(`${this.baseUrl}/Dismiss`, options)
                                .then(response => {
                                    return response.ok ? resolve(true) : reject("Not dismissed");
                                })
                                .catch(error => {
                                    console.log(error);
                                    return reject(error)
                                });
                        })
                        .catch((error) => {
                            if (error instanceof InteractionRequiredAuthError) {
                                // Let centralized auth handling manage redirects
                            }
                            console.log(error);
                            return reject(error)
                        });
                });
            });
    };
}